import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { formatSar } from '../lib/marketPricing';
import { categoryName, EXCLUSIVES_PAGE, exclusiveFromSar, exclusiveImage, exclusiveTitle, exclusivesPublic, fetchExclusiveCategories, fetchExclusives } from '../lib/marketExclusives';
import MarketShell from '../components/market/MarketShell';

// /market/exclusives — «حصريات معبر» (owner, 2026-10-29): products the MAABAR
// team sells itself, on a page of their own, apart from the catalogue and its
// cart. A product here is not bought from a basket: the trader opens a chat,
// negotiates everything (shipping included) and pays the invoice the team
// sends in that chat. See lib/marketExclusives.js.
// The seller's own categories (2026-10-29) sit above the products as a bar —
// «الكل» first, then only categories that have products, in the seller's order.

export const EXCL_T = {
  ar: {
    title: 'حصريات معبر', sub: 'منتجات يختارها فريق معبر بنفسه. تواصل معنا مباشرة وتفاوض على السعر والكمية والشحن.',
    hiddenNote: 'مخفي عن العملاء — تشوفه لأنك من فريق معبر. يفتحه الأدمن من لوحة الإدارة ← حصريات معبر.',
    market: 'السوق', all: 'الكل', badge: 'حصري', from: 'يبدأ من', onRequest: 'السعر عند التواصل', moq: 'الحد الأدنى', more: 'عرض المزيد',
    none: 'لا توجد منتجات حصرية الآن.', failed: 'تعذّر تحميل الحصريات، حاول مرة أخرى.',
    how: [['تصفّح', 'منتجات مختارة من فريق معبر'], ['تواصل', 'محادثة مباشرة مع ترجمة تلقائية'], ['تفاوض', 'على السعر والكمية والشحن'], ['ادفع', 'فاتورة بالريال تصلك في المحادثة']],
  },
  en: {
    title: 'MAABAR Exclusives', sub: 'Products the MAABAR team picks itself. Talk to us directly and negotiate price, quantity and shipping.',
    hiddenNote: 'Hidden from customers — you see it because you are on the MAABAR team. An admin opens it in the console → MAABAR Exclusives.',
    market: 'Market', all: 'All', badge: 'Exclusive', from: 'From', onRequest: 'Price on contact', moq: 'Min. order', more: 'Show more',
    none: 'No exclusive products right now.', failed: 'Could not load the exclusives — please try again.',
    how: [['Browse', 'Products picked by the MAABAR team'], ['Talk', 'Direct chat with auto-translation'], ['Negotiate', 'Price, quantity and shipping'], ['Pay', 'A SAR invoice sent in the chat']],
  },
  zh: {
    title: 'MAABAR 独家', sub: '由 MAABAR 团队亲自挑选的产品。直接联系我们，就价格、数量和运费进行洽谈。',
    hiddenNote: '对客户隐藏——您能看到是因为您是 MAABAR 团队成员。管理员可在后台 → MAABAR 独家 中开放。',
    market: '市场', all: '全部', badge: '独家', from: '起', onRequest: '价格面议', moq: '起订量', more: '显示更多',
    none: '暂无独家产品。', failed: '无法加载独家产品，请重试。',
    how: [['浏览', 'MAABAR 团队精选产品'], ['沟通', '直接聊天，自动翻译'], ['洽谈', '价格、数量和运费'], ['付款', '聊天中发送的沙特里亚尔发票']],
  },
};

export function ExclusiveCard({ p, lang = 'ar' }) {
  const nav = useNavigate();
  const c = EXCL_T[lang] || EXCL_T.ar;
  const title = exclusiveTitle(p, lang);
  const sar = exclusiveFromSar(p);
  const moq = Number(p.moq) || 0;
  const open = () => nav(`/market/exclusives/${p.id}`);
  return (
    <article className="mk-card mk-excl-card" data-exclusive-card={p.id}>
      <button type="button" className="mk-card-media" onClick={open} aria-label={title}>
        {exclusiveImage(p) ? <img src={exclusiveImage(p)} alt={title} loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : <span className="mk-thumb-ph" aria-hidden="true" />}
        <span className="mk-excl-badge">{c.badge}</span>
      </button>
      <div className="mk-card-body">
        <h3 className="mk-card-title" onClick={open} title={title}>{title}</h3>
        <p className="mk-price" data-excl-price={sar ?? ''}>
          {sar != null ? <><small>{c.from} </small><span className="mk-ltr">{formatSar(sar)}</span></> : <small>{c.onRequest}</small>}
        </p>
        {moq > 0 && <p className="mk-moq">{c.moq}: {moq.toLocaleString('en-US')}</p>}
      </div>
    </article>
  );
}

export default function MarketExclusives({ lang = 'ar', user }) {
  const nav = useNavigate();
  const c = EXCL_T[lang] || EXCL_T.ar;
  usePageTitle('market', lang);
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState('');
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(null);   // null = «الكل»
  const [pub, setPub] = useState(true);   // the section is open to customers
  useEffect(() => { let alive = true; exclusivesPublic().then((v) => { if (alive) setPub(v); }); return () => { alive = false; }; }, []);
  useEffect(() => { let alive = true; fetchExclusiveCategories().then((x) => { if (alive) setCats(x); }); return () => { alive = false; }; }, []);
  useEffect(() => {
    let alive = true;
    setRows(null);
    fetchExclusives({ categoryId: cat }).then((r) => { if (!alive) return; setRows(r.rows); setTotal(r.total); setErr(r.error ? c.failed : ''); });
    return () => { alive = false; };
  }, [c.failed, cat]);
  const more = async () => {
    const r = await fetchExclusives({ offset: rows.length, categoryId: cat });
    setRows([...rows, ...r.rows]);
  };
  return (
    <MarketShell lang={lang} user={user}>
      <div className="mk-wrap" style={{ paddingBottom: 48 }}>
        <div className="mk-crumbs" aria-label="breadcrumb">
          <button type="button" onClick={() => nav('/market')}>{c.market}</button>
          <span aria-hidden="true">›</span><span aria-current="page">{c.title}</span>
        </div>
        {/* Hidden from customers (2026-10-29): the server shows the page only
            to the team then — this says so to them, so nobody thinks it is live. */}
        {!pub && rows && rows.length > 0 && <p className="mk-excl-hidden" data-exclusives-hidden="1">{c.hiddenNote}</p>}
        <header className="mk-excl-hero" data-exclusives-hero="1">
          <div>
            <span className="mk-excl-kicker">{c.badge}</span>
            <h1>{c.title}</h1>
            <p>{c.sub}</p>
          </div>
          <ol className="mk-excl-how">
            {c.how.map(([b, s], i) => <li key={b}><span className="mk-excl-step">{i + 1}</span><span><b>{b}</b><span>{s}</span></span></li>)}
          </ol>
        </header>
        {cats.length > 0 && (
          <div className="mk-pills mk-excl-cats" role="tablist" data-exclusive-cats={cats.length}>
            <button type="button" role="tab" aria-selected={cat === null} className={`mk-pill${cat === null ? ' on' : ''}`} data-exclusive-cat="all" onClick={() => setCat(null)}>{c.all}</button>
            {cats.map((x) => (
              <button key={x.id} type="button" role="tab" aria-selected={cat === x.id} className={`mk-pill${cat === x.id ? ' on' : ''}`} data-exclusive-cat={x.id} onClick={() => setCat(x.id)}>
                {categoryName(x, lang)}
              </button>
            ))}
          </div>
        )}
        {rows === null ? (
          <div className="mk-grid cols-4" style={{ marginTop: 16 }}>{Array.from({ length: 8 }, (_, i) => <div key={i} className="mk-skel" style={{ aspectRatio: '3 / 4' }} />)}</div>
        ) : err ? <p className="mk-empty" data-exclusives-error="1">{err}</p>
          : rows.length === 0 ? <p className="mk-empty" data-exclusives-empty="1">{c.none}</p>
          : (
            <>
              <div className="mk-grid cols-4" data-exclusives-rows={rows.length} style={{ marginTop: 16 }}>
                {rows.map((p) => <ExclusiveCard key={p.id} p={p} lang={lang} />)}
              </div>
              {rows.length < total && rows.length % EXCLUSIVES_PAGE === 0 && <div style={{ textAlign: 'center', marginTop: 18 }}><button type="button" className="mk-btn ghost" onClick={more}>{c.more}</button></div>}
            </>
          )}
      </div>
    </MarketShell>
  );
}
